import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import BusinessDetail from './pages/BusinessDetail';
import BoardDetail from './pages/BoardDetail';
import AdminInbox from './pages/AdminInbox';
import AdminSettings from './pages/AdminSettings';
import Clients from './pages/Clients';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TrashPage from './pages/TrashPage';
import TaskManager from './pages/TaskManager';
import ProjectDetail from './pages/ProjectDetail';
import ProjectMembers from './pages/ProjectMembers';
import AdminLayout from './components/AdminLayout';
import PublicReservationWidget from './components/PublicReservationWidget';
import PublicBusinessLanding from './components/PublicBusinessLanding';
import CatLoadingSpinner from './components/CatLoadingSpinner';
import Landing from './pages/Landing';
import LiveDashboard from './pages/LiveDashboard';
import NewReservation from './pages/NewReservation';
import { CatAssistantProvider } from './contexts/CatAssistantContext';
import CatAssistant from './components/CatAssistant';
import axios from 'axios';
import './cat-theme.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <CatLoadingSpinner message="Verificando credenciales..." />;
  return user ? <>{children}</> : <Navigate to="/login" />;
};

const SlugRedirect: React.FC = () => {
  const { businessSlug, boardSlug } = useParams<{ businessSlug: string; boardSlug?: string }>();
  const [boardId, setBoardId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  useEffect(() => {
    const fetchBoardId = async () => {
      try {
        if (boardSlug) {
          // Fetch boardId from businessSlug/boardSlug
          const response = await axios.get(`/api/public/slug/${businessSlug}/${boardSlug}`);
          setBoardId(response.data.boardId);
        } else {
          // If only business slug, show business landing page
          setBoardId(null); // No boardId means show business landing
        }
      } catch (err) {
        console.error('Error resolving slug:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBoardId();
  }, [businessSlug, boardSlug]);

  if (loading) return <CatLoadingSpinner message="Los gatitos están buscando tu agenda..." />;
  if (error) return <Navigate to="/dashboard" replace />;

  // If no boardSlug, show business landing page
  if (!boardSlug) {
    return <PublicBusinessLanding businessSlug={businessSlug} />;
  }

  // If boardSlug exists, render PublicReservationWidget with the resolved boardId
  if (!boardId) return <Navigate to="/dashboard" replace />;
  return <PublicReservationWidget boardId={boardId} />;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <SocketProvider>
          <CatAssistantProvider>
            <Router>
              <CatAssistant />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/business/:businessId" element={<BusinessDetail />} />
                  <Route path="/board/:boardId" element={<BoardDetail />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/admin/inbox" element={<AdminInbox />} />
                  <Route path="/admin/live" element={<LiveDashboard />} />
                  <Route path="/admin/new-reservation" element={<NewReservation />} />
                  <Route path="/nuevoturno" element={<NewReservation />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/superadmin" element={<SuperAdminDashboard />} />
                  <Route path="/trash" element={<TrashPage />} />
                  <Route path="/tasks" element={<TaskManager />} />
                  <Route path="/tasks/project/:projectId" element={<ProjectDetail />} />
                  <Route path="/tasks/project/:projectId/members" element={<ProjectMembers />} />
                </Route>
                <Route path="/public/board/:boardId" element={<PublicReservationWidget />} />
                {/* Slug-based routes for sharing */}
                <Route path="/:businessSlug" element={<SlugRedirect />} />
                <Route path="/:businessSlug/:boardSlug" element={<SlugRedirect />} />
                <Route path="/" element={<Landing />} />
              </Routes>
            </Router>
          </CatAssistantProvider>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
